import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'

export type ConfigurableListColumnOption<TId extends string = string> = {
  id: TId
  label: string
}

export type ConfigurableListColumnSettings<TId extends string = string> = {
  visibleColumnIds: TId[]
  columnOrderIds: TId[]
}

export type UseConfigurableListColumnsOptions<TId extends string> = {
  storageKey: string
  columnOptions: readonly ConfigurableListColumnOption<TId>[]
  lockedColumnIds?: readonly TId[]
  /** When false, skip localStorage read/write (e.g. Columns UI disabled). */
  persist?: boolean
}

const EMPTY_LOCKED_COLUMN_IDS: readonly never[] = []

function withLockedColumnIds<TId extends string>(
  columnIds: TId[],
  lockedColumnIds: readonly TId[],
): TId[] {
  if (lockedColumnIds.length === 0) return columnIds
  const lockedSet = new Set(lockedColumnIds)
  const withoutLocked = columnIds.filter((id) => !lockedSet.has(id))
  return [...lockedColumnIds, ...withoutLocked]
}

function normalizeColumnIds<TId extends string>(
  columnIds: unknown,
  allColumnIds: readonly TId[],
  lockedColumnIds: readonly TId[],
): TId[] {
  const known = new Set<string>(allColumnIds)
  const ordered: TId[] = []
  if (Array.isArray(columnIds)) {
    for (const id of columnIds) {
      if (typeof id === 'string' && known.has(id) && !ordered.includes(id as TId)) {
        ordered.push(id as TId)
      }
    }
  }
  for (const id of allColumnIds) {
    if (!ordered.includes(id)) ordered.push(id)
  }
  return withLockedColumnIds(ordered, lockedColumnIds)
}

function loadColumnSettings<TId extends string>(
  storageKey: string,
  allColumnIds: readonly TId[],
  lockedColumnIds: readonly TId[],
): ConfigurableListColumnSettings<TId> {
  const fallback: ConfigurableListColumnSettings<TId> = {
    visibleColumnIds: [...allColumnIds],
    columnOrderIds: [...allColumnIds],
  }
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<ConfigurableListColumnSettings<TId>>
    const orderIds = normalizeColumnIds(parsed.columnOrderIds, allColumnIds, lockedColumnIds)
    const savedVisible = Array.isArray(parsed.visibleColumnIds)
      ? parsed.visibleColumnIds
      : allColumnIds
    return {
      visibleColumnIds: withLockedColumnIds(
        orderIds.filter((columnId) => savedVisible.includes(columnId)),
        lockedColumnIds,
      ),
      columnOrderIds: orderIds,
    }
  } catch {
    return fallback
  }
}

function reorderColumnIds<TId extends string>(
  columnIds: TId[],
  draggedId: TId,
  targetId: TId,
  lockedColumnIds: readonly TId[],
): TId[] {
  if (draggedId === targetId) return columnIds
  const lockedSet = new Set(lockedColumnIds)
  if (lockedSet.has(draggedId) || lockedSet.has(targetId)) return columnIds
  const next = columnIds.filter((id) => id !== draggedId)
  const targetIndex = next.indexOf(targetId)
  if (targetIndex < 0) return columnIds
  next.splice(targetIndex, 0, draggedId)
  return withLockedColumnIds(next, lockedColumnIds)
}

/**
 * Reimbursements / resident-style column visibility + drag reorder, persisted in localStorage.
 */
export function useConfigurableListColumns<TId extends string>({
  storageKey,
  columnOptions,
  lockedColumnIds = EMPTY_LOCKED_COLUMN_IDS as readonly TId[],
  persist = true,
}: UseConfigurableListColumnsOptions<TId>) {
  const allColumnIds = useMemo(
    () => columnOptions.map((column) => column.id),
    [columnOptions],
  )
  const lockedIds = useMemo(() => [...lockedColumnIds], [lockedColumnIds])

  const [isColumnsOpen, setIsColumnsOpen] = useState(false)
  const [columnSearchTerm, setColumnSearchTerm] = useState('')
  const [columnSettings, setColumnSettings] = useState<
    ConfigurableListColumnSettings<TId>
  >(() =>
    persist
      ? loadColumnSettings(storageKey, allColumnIds, lockedIds)
      : {
          visibleColumnIds: [...allColumnIds],
          columnOrderIds: [...allColumnIds],
        },
  )
  const [draggedColumnId, setDraggedColumnId] = useState<TId | null>(null)
  const [dragPreviewColumnOrderIds, setDragPreviewColumnOrderIds] = useState<
    TId[] | null
  >(null)

  const columnsMenuRef = useRef<HTMLDivElement | null>(null)
  const columnsListRef = useRef<HTMLDivElement | null>(null)

  const { visibleColumnIds, columnOrderIds } = columnSettings
  const dropdownColumnOrderIds = dragPreviewColumnOrderIds ?? columnOrderIds

  useEffect(() => {
    if (!persist) return
    localStorage.setItem(storageKey, JSON.stringify(columnSettings))
  }, [columnSettings, persist, storageKey])

  useEffect(() => {
    if (!isColumnsOpen) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (target && columnsMenuRef.current?.contains(target)) return
      setIsColumnsOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsColumnsOpen(false)
    }
    document.addEventListener('click', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('click', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isColumnsOpen])

  const lockedSet = useMemo(() => new Set(lockedIds), [lockedIds])
  const availableColumnOptions = useMemo(
    () => columnOptions.filter((column) => !lockedSet.has(column.id)),
    [columnOptions, lockedSet],
  )
  const defaultCustomizableColumnIds = useMemo(
    () => availableColumnOptions.map((column) => column.id),
    [availableColumnOptions],
  )

  const visibleColumnSet = useMemo(
    () => new Set<TId>(visibleColumnIds),
    [visibleColumnIds],
  )
  const selectedAvailableColumnCount = availableColumnOptions.filter((column) =>
    visibleColumnSet.has(column.id),
  ).length
  const areAllAvailableColumnsSelected =
    selectedAvailableColumnCount === availableColumnOptions.length
  const areNoAvailableColumnsSelected = selectedAvailableColumnCount === 0

  const filteredColumnOptions = useMemo(() => {
    const needle = columnSearchTerm.trim().toLowerCase()
    const orderedOptions = dropdownColumnOrderIds
      .map((id) => columnOptions.find((column) => column.id === id))
      .filter(
        (column): column is ConfigurableListColumnOption<TId> =>
          Boolean(column && !lockedSet.has(column.id)),
      )
    if (!needle) return orderedOptions
    return orderedOptions.filter(
      (column) =>
        column.label.toLowerCase().includes(needle) ||
        column.id.toLowerCase().includes(needle),
    )
  }, [columnOptions, columnSearchTerm, dropdownColumnOrderIds, lockedSet])

  const setVisibleColumns = useCallback(
    (columnIds: TId[]) => {
      setColumnSettings((currentSettings) => ({
        ...currentSettings,
        visibleColumnIds: withLockedColumnIds(
          currentSettings.columnOrderIds.filter((columnId) =>
            columnIds.includes(columnId),
          ),
          lockedIds,
        ),
      }))
    },
    [lockedIds],
  )

  const toggleColumn = useCallback(
    (columnId: TId) => {
      if (lockedSet.has(columnId)) return
      setColumnSettings((currentSettings) => {
        const nextColumnIds = currentSettings.visibleColumnIds.includes(columnId)
          ? currentSettings.visibleColumnIds.filter((id) => id !== columnId)
          : [...currentSettings.visibleColumnIds, columnId]
        return {
          ...currentSettings,
          visibleColumnIds: withLockedColumnIds(nextColumnIds, lockedIds),
        }
      })
    },
    [lockedIds, lockedSet],
  )

  const handleColumnDragStart = useCallback(
    (event: DragEvent, columnId: TId) => {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', columnId)
      setDraggedColumnId(columnId)
      setDragPreviewColumnOrderIds(columnOrderIds)
    },
    [columnOrderIds],
  )

  const autoScrollColumnsList = useCallback((clientY: number) => {
    const list = columnsListRef.current
    if (!list) return
    const rect = list.getBoundingClientRect()
    const edge = 36
    if (clientY < rect.top + edge) list.scrollTop -= 12
    else if (clientY > rect.bottom - edge) list.scrollTop += 12
  }, [])

  const handleColumnDragOver = useCallback(
    (event: DragEvent, targetId: TId) => {
      event.preventDefault()
      autoScrollColumnsList(event.clientY)
      if (!draggedColumnId) return
      setDragPreviewColumnOrderIds((currentOrderIds) =>
        reorderColumnIds(
          currentOrderIds ?? columnOrderIds,
          draggedColumnId,
          targetId,
          lockedIds,
        ),
      )
    },
    [autoScrollColumnsList, columnOrderIds, draggedColumnId, lockedIds],
  )

  const handleColumnDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      if (!dragPreviewColumnOrderIds) {
        setDraggedColumnId(null)
        return
      }
      setColumnSettings((currentSettings) => ({
        ...currentSettings,
        columnOrderIds: dragPreviewColumnOrderIds,
        visibleColumnIds: withLockedColumnIds(
          dragPreviewColumnOrderIds.filter((id) =>
            currentSettings.visibleColumnIds.includes(id),
          ),
          lockedIds,
        ),
      }))
      setDraggedColumnId(null)
      setDragPreviewColumnOrderIds(null)
    },
    [dragPreviewColumnOrderIds, lockedIds],
  )

  const resetColumnsToDefault = useCallback(() => {
    setColumnSettings({
      visibleColumnIds: [...allColumnIds],
      columnOrderIds: [...allColumnIds],
    })
  }, [allColumnIds])

  const tableColumnIds = useMemo(() => {
    return columnOrderIds.filter(
      (columnId) => lockedSet.has(columnId) || visibleColumnSet.has(columnId),
    )
  }, [columnOrderIds, lockedSet, visibleColumnSet])

  return {
    isColumnsOpen,
    setIsColumnsOpen,
    columnSearchTerm,
    setColumnSearchTerm,
    visibleColumnIds,
    columnOrderIds,
    tableColumnIds,
    columnsMenuRef,
    columnsListRef,
    availableColumnOptions,
    filteredColumnOptions,
    selectedAvailableColumnCount,
    areAllAvailableColumnsSelected,
    areNoAvailableColumnsSelected,
    visibleColumnSet,
    draggedColumnId,
    setDraggedColumnId,
    setDragPreviewColumnOrderIds,
    setVisibleColumns,
    toggleColumn,
    handleColumnDragStart,
    handleColumnDragOver,
    handleColumnDrop,
    autoScrollColumnsList,
    resetColumnsToDefault,
    defaultCustomizableColumnIds,
    lockedColumnIds: lockedIds,
  }
}

export type ConfigurableListColumnsController<TId extends string = string> =
  ReturnType<typeof useConfigurableListColumns<TId>>

/**
 * Order and filter a column list using saved visibility settings.
 * Trailing columns (e.g. Action) are always appended and excluded from customization.
 */
export function applyColumnVisibilityOrder<T extends { id: string }>(
  columns: readonly T[],
  tableColumnIds: readonly string[],
  trailingColumnIds: readonly string[] = [],
): T[] {
  const trailingSet = new Set(trailingColumnIds)
  const byId = new Map(columns.map((column) => [column.id, column]))
  const ordered = tableColumnIds
    .filter((id) => !trailingSet.has(id))
    .map((id) => byId.get(id))
    .filter((column): column is T => Boolean(column))
  for (const trailingId of trailingColumnIds) {
    const trailing = byId.get(trailingId)
    if (trailing) ordered.push(trailing)
  }
  return ordered
}
